import { useState } from "react";
import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import type { RunSummaryRow } from "@shared/types/runs";
import { partlyFailedDetail } from "@shared/runs/partlyFailed";
import {
  connectionsWhatRan,
  formatDuration,
  runPartlyFailedFacts,
  runStatusPresentation,
  transformWhatRan,
} from "@/components/runs/runFormatting";
import { usePermissions } from "@/hooks/use-permissions";
import { StatusCell } from "@/components/runs/StatusCell";
import { StartedByCell } from "@/components/runs/RunsTable";
import { startedText } from "@/components/runs/runFormatting";
import { RunningDuration } from "@/components/runs/RunningDuration";
import { useRunsPage } from "@/hooks/use-runs";
import { OVERVIEW_RECENT_RUNS } from "@shared/types/overview";
import { pixelColumnLayout } from "@/lib/grid";
import { LogsIcon } from "@/lib/icons";
import { ROUTES } from "@/lib/routes";
import { OverviewCard, OverviewLink } from "./OverviewCard";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { ZoneCaption } from "@/components/shell/ZoneCaption";

/**
 * Last five runs (contract §3, card 8): the same rows the Runs screen shows (`recent` is
 * `RunService.list({ limit: 5 })`), in a `SkyDataGrid`, and as a list at phone width, where a
 * four-column grid would scroll sideways inside a card.
 *
 * Read-only: no cancel, retry or export here. Those live on the Runs screen, one click away.
 */

/** What ran, in words: the connections, then the transformations. */
export function recentWhatRan(
  row: RunSummaryRow,
  connectionNames: ReadonlyMap<string, string> | null,
): string {
  const names = connectionNames ?? new Map<string, string>();
  const known = new Map(
    row.dataflowIds.map((id) => [
      id,
      names.get(id) ?? (connectionNames ? "Deleted connection" : "Connection"),
    ]),
  );
  const connections = connectionsWhatRan(row.dataflowIds, known);
  const transform =
    row.type === "transform" || row.type === "import_transform"
      ? transformWhatRan(row.dbtJobNames)
      : null;
  const parts = [connections, transform].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" · ") : "No connection";
}

/**
 * Round 6 (W6): a run with a dbt step has two durations; "Run 1m 14s · dbt step 1m 04s" names
 * both, the same words the Runs list and the Transformations screens use. The run's alone
 * otherwise, as before.
 */
export function recentDuration(row: RunSummaryRow): string {
  if (
    row.transformDurationSeconds !== undefined &&
    row.durationSeconds !== null &&
    row.durationSeconds !== undefined
  ) {
    return `Run ${formatDuration(row.durationSeconds)} · dbt step ${formatDuration(row.transformDurationSeconds)}`;
  }
  return formatDuration(row.durationSeconds);
}

/**
 * "No runs yet." and where a run comes from (round 6 walk W23): a link only to a screen the role
 * can act on (`runs.start` for an import from Connections, `schedules.write` for a schedule), as
 * the first-run checklist does; a role with neither is told how runs appear.
 */
function NoRunsYet() {
  const { canAny } = usePermissions();
  const canStart = canAny("runs.start");
  const canSchedule = canAny("schedules.write");
  const connections = <OverviewLink to={ROUTES.connections}>Connections</OverviewLink>;
  const schedules = <OverviewLink to={ROUTES.schedules}>Schedules</OverviewLink>;
  if (canStart && canSchedule) {
    return (
      <>
        No runs yet. Start an import from {connections}, or give a connection a time on {schedules}.
      </>
    );
  }
  if (canStart) return <>No runs yet. Start an import from {connections}.</>;
  if (canSchedule) return <>No runs yet. Give a connection a time on {schedules}.</>;
  return <>No runs yet. Runs appear here when a schedule fires or someone starts an import.</>;
}

function StatusBadge({ row }: { row: RunSummaryRow }) {
  const status = runStatusPresentation(row.status);
  return (
    <SkyBadge appearance="tint" color={status.color}>
      {status.label}
    </SkyBadge>
  );
}

/** Round 10 (O1, O2): whose runs the card lists. */
type RunScope = "all" | "mine";

/** The All/Mine toggle: two buttons in one named group, the pressed one filled. */
function ScopeToggle({
  scope,
  onChange,
}: {
  scope: RunScope;
  onChange: (scope: RunScope) => void;
}) {
  return (
    <span
      role="group"
      aria-label="Whose runs"
      className="inline-flex gap-1"
      data-testid="overview-recent-scope"
    >
      {(["all", "mine"] as const).map((value) => (
        <SkyButton
          key={value}
          type="button"
          size="small"
          appearance={scope === value ? "primary" : "subtle"}
          aria-pressed={scope === value}
          onClick={() => onChange(value)}
          testId={`overview-recent-scope-${value}`}
        >
          {value === "all" ? "All" : "Mine"}
        </SkyButton>
      ))}
    </span>
  );
}

/**
 * Last five runs (round 10, O1, O2): All keeps the overview's own `recent`; Mine reads
 * `GET /runs?limit=5&startedBy=me`, a read mounted only while Mine is on, so the card costs
 * nothing extra otherwise. The choice is not remembered.
 */
export function RecentRunsCard({
  rows,
  connectionNames,
}: {
  rows: RunSummaryRow[];
  /** Null while the connection list is loading, or when the role cannot read it. */
  connectionNames: ReadonlyMap<string, string> | null;
}) {
  const [scope, setScope] = useState<RunScope>("all");
  // The frame (and the toggle in it) stays mounted across All and Mine, so focus stays on the
  // button just pressed; only the body swaps.
  return (
    <OverviewCard
      id="recent"
      title="Last five runs"
      icon={LogsIcon}
      testId="overview-recent"
      action={
        <span className="inline-flex items-center gap-3">
          <ScopeToggle scope={scope} onChange={setScope} />
          <OverviewLink to={scope === "mine" ? `${ROUTES.runs}?startedBy=me` : ROUTES.runs}>
            All runs
          </OverviewLink>
        </span>
      }
    >
      {scope === "mine" ? (
        <MineRunsBody connectionNames={connectionNames} />
      ) : (
        <RunsBody rows={rows} connectionNames={connectionNames} scope="all" />
      )}
    </OverviewCard>
  );
}

function MineRunsBody({
  connectionNames,
}: {
  connectionNames: ReadonlyMap<string, string> | null;
}) {
  const mine = useRunsPage({ startedBy: "me" }, null, OVERVIEW_RECENT_RUNS);
  return (
    <RunsBody
      rows={mine.data?.rows ?? []}
      connectionNames={connectionNames}
      scope="mine"
      status={mine.isError ? "error" : mine.isPending ? "loading" : "ready"}
    />
  );
}

function RunsBody({
  rows,
  connectionNames,
  scope,
  status = "ready",
}: {
  rows: RunSummaryRow[];
  connectionNames: ReadonlyMap<string, string> | null;
  scope: RunScope;
  status?: "ready" | "loading" | "error";
}) {
  const zone = useDisplayTz();
  const columns: SkyDataGridColumn<RunSummaryRow>[] = [
    {
      key: "status",
      header: "Status",
      width: "20%",
      // `StatusCell`: a badge straight in a cell is stretched across the column (round 6: this
      // grid never showed at desktop width before the W1 cascade fix, so it was never seen).
      renderCell: (_value, row) => (
        <StatusCell
          badge={<StatusBadge row={row} />}
          secondary={
            row.status === "partially_succeeded" ? (
              // #817: what failed, as the Runs list says it under the same badge.
              <SkyText
                size={200}
                color="muted"
                block
                className="break-words"
                testId={`overview-recent-partly-failed-${row.runId}`}
              >
                {partlyFailedDetail(runPartlyFailedFacts(row, connectionNames ?? undefined))}
              </SkyText>
            ) : undefined
          }
          testId={`overview-recent-status-${row.runId}`}
        />
      ),
    },
    {
      key: "whatRan",
      header: "What ran",
      width: "30%",
      // Round 7 T-37: a grid's primary cell is 14 px (it inherited the page's 16).
      renderCell: (_value, row) => (
        <SkyText size={300}>
          <OverviewLink
            to={`${ROUTES.runs}/${row.runId}`}
            testId={`overview-recent-run-${row.runId}`}
          >
            {recentWhatRan(row, connectionNames)}
          </OverviewLink>
        </SkyText>
      ),
    },
    {
      key: "startedBy",
      header: "Started by",
      width: "20%",
      renderCell: (_value, row) => <StartedByCell row={row} />,
    },
    {
      key: "createdAt",
      header: "Started",
      width: "20%",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {startedText(row, zone)}
        </SkyText>
      ),
    },
    {
      key: "durationSeconds",
      header: "Duration",
      // #1196: a pixel width holds the whole header (label plus 32 px of padding).
      width: "104px",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric" testId={`overview-recent-duration-${row.runId}`}>
          <RunningDuration
            seconds={row.durationSeconds}
            runningSince={row.status === "running" ? row.startedAt : null}
            known={recentDuration(row)}
          />
        </SkyText>
      ),
    },
  ];

  return (
    <>
      {status === "loading" ? (
        <SkyText size={300} color="muted" block testId="overview-recent-loading">
          Loading your runs…
        </SkyText>
      ) : status === "error" ? (
        <SkyText size={300} color="muted" block testId="overview-recent-error">
          Your runs could not be loaded. Try again in a moment.
        </SkyText>
      ) : rows.length === 0 ? (
        <SkyText size={300} color="muted" block testId="overview-recent-empty">
          {scope === "mine" ? <>You have not started a run yet.</> : <NoRunsYet />}
        </SkyText>
      ) : (
        <>
          {/* Round 6 walk W1: no `hidden md:block` / `flex md:hidden` pair. skypoint-ui ships
              `.hidden`, `.block` and `.flex` after the app's `md:` rules, so the grid never
              showed and the phone list showed at every width (`libraryCascade.test.ts`). Each
              element names only the width it hides at. */}
          <div className="max-md:hidden">
            <SkyDataGrid<RunSummaryRow>
              data={rows}
              columns={
                pixelColumnLayout(columns, { minimums: {}, defaultMinimum: 100, selection: false })
                  .columns
              }
              getRowId={(row) => row.runId}
              emptyMessage="No runs yet"
              testId="overview-recent-grid"
            />
          </div>
          <ul className="divide-y divide-border md:hidden" data-testid="overview-recent-list">
            {rows.map((row) => (
              <li key={row.runId} className="flex flex-col gap-1 py-2">
                <div className="flex items-center justify-between gap-2">
                  <SkyText size={300}>
                    <OverviewLink to={`${ROUTES.runs}/${row.runId}`}>
                      {recentWhatRan(row, connectionNames)}
                    </OverviewLink>
                  </SkyText>
                  <StatusBadge row={row} />
                </div>
                <SkyText size={200} color="muted" font="numeric" block>
                  {`${startedText(row, zone)} · `}
                  <RunningDuration
                    seconds={row.durationSeconds}
                    runningSince={row.status === "running" ? row.startedAt : null}
                    known={recentDuration(row)}
                  />
                </SkyText>
                <StartedByCell row={row} />
              </li>
            ))}
          </ul>
          {/* B-51 (round 9): the times above are in the reader's zone, said once. */}
          <ZoneCaption testId="overview-recent-zone-caption" className="mt-2" />
        </>
      )}
    </>
  );
}
